/* Libraries */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { formatDate } from '~/src/utils/date';
import { autorun } from 'mobx';

/* Components */
import SettingsBilling from '~/src/components/Settings/SettingsBilling';

/* Hooks */
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import useUser from '~/src/hooks/useUser';

/* States */
import { ERROR_STATE, PENDING_STATE } from '~/src/stores/userStore/states';

const SettingsBillingContainer = observer(() => {
  const { currentOrganization } = useUser();
  const { isCbsIntegrationEnabled } = useFeatureAccesses();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creditNotesPage, setCreditNotesPage] = useState(1);

  useEffect(() => {
    if (isCbsIntegrationEnabled) {
      currentOrganization.fetchCBSInvoices(currentPage);
    } else {
      currentOrganization.fetchReceipts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isCbsIntegrationEnabled]);

  useEffect(() => {
    if (selectedInvoice) {
      currentOrganization.fetchCBSCreditNotesByInvoice(
        selectedInvoice.id,
        creditNotesPage,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoice, creditNotesPage]);

  useEffect(() => {
    const disposer = autorun(() => {
      if (
        isCbsIntegrationEnabled &&
        currentOrganization.cbsInvoices &&
        currentOrganization.cbsInvoices.total_pages
      ) {
        const newTotalPages = Math.max(
          1,
          currentOrganization.cbsInvoices.total_pages,
        );
        setTotalPages(newTotalPages);
      }
    });

    return () => {
      disposer();
    };
  }, [isCbsIntegrationEnabled, currentOrganization]);

  let receipts = [];
  if (currentOrganization.receipts && !isCbsIntegrationEnabled) {
    receipts = currentOrganization.receipts.map(
      ({ created, amount_captured, receipt_url }) => ({
        created: formatDate(new Date(created * 1000)),
        amount_captured: `$${amount_captured / 100}`,
        receipt_url,
      }),
    );
  } else if (currentOrganization.cbsInvoices && isCbsIntegrationEnabled) {
    const invoicesData = currentOrganization.cbsInvoices.invoices;
    const orgFprint = currentOrganization.fprint;
    if (orgFprint && invoicesData) {
      receipts = invoicesData.map(
        ({ id, currency, due_at, total, has_credit_notes }) => ({
          id,
          created: formatDate(new Date(due_at * 1000)),
          amount_captured: new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
          }).format(total / 100),
          receipt_url: `/cbs_subscriptions/org/${orgFprint}/invoices/${id}.pdf`,
          has_credit_notes: !!has_credit_notes,
        }),
      );
    }
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleViewCreditNotes = (invoice) => {
    setSelectedInvoice(invoice);
    setCreditNotesPage(1);
  };

  const handleBack = () => {
    setSelectedInvoice(null);
    setCreditNotesPage(1);
  };

  const selectedInvoiceKey = selectedInvoice
    ? String(selectedInvoice.id)
    : null;
  const cachedCreditNotes = selectedInvoiceKey
    ? currentOrganization.cbsCreditNotesByInvoice.get(selectedInvoiceKey)
    : null;
  const orgState = currentOrganization.state;
  const isFetchingCreditNotes =
    !!selectedInvoiceKey && orgState === PENDING_STATE;
  const hasCreditNotesError = !!selectedInvoiceKey && orgState === ERROR_STATE;
  const isFetchingInvoices = !selectedInvoiceKey && orgState === PENDING_STATE;
  const creditNotesTotalPages = Math.max(
    1,
    cachedCreditNotes?.total_pages ?? 1,
  );

  const handleCreditNotesPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= creditNotesTotalPages) {
      setCreditNotesPage(newPage);
    }
  };

  let invoiceCreditNotes = [];
  if (cachedCreditNotes?.credit_notes && currentOrganization.fprint) {
    const orgFprint = currentOrganization.fprint;
    invoiceCreditNotes = cachedCreditNotes.credit_notes.map(
      ({ id, currency, stripe_created_at, total }) => ({
        id,
        created: formatDate(new Date(stripe_created_at)),
        amount_captured: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
        }).format(total / 100),
        receipt_url: `/cbs_subscriptions/org/${orgFprint}/credit_notes/${id}.pdf`,
      }),
    );
  }

  const paginationProps = isCbsIntegrationEnabled
    ? {
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
      }
    : null;

  const creditNotesPaginationProps = {
    currentPage: creditNotesPage,
    totalPages: creditNotesTotalPages,
    onPageChange: handleCreditNotesPageChange,
  };

  return (
    <SettingsBilling
      receipts={receipts}
      pagination={paginationProps}
      showInvoiceActions={isCbsIntegrationEnabled}
      isFetchingInvoices={isFetchingInvoices}
      selectedInvoice={selectedInvoice}
      invoiceCreditNotes={invoiceCreditNotes}
      creditNotesPagination={creditNotesPaginationProps}
      isFetchingCreditNotes={isFetchingCreditNotes}
      hasCreditNotesError={hasCreditNotesError}
      onViewCreditNotes={handleViewCreditNotes}
      onBack={handleBack}
    />
  );
});

export default SettingsBillingContainer;
